import * as THREE from 'three';
import { resolveLayoutAnchorWorldPosition } from './entityAnchorResolution.js';
import { resolveRingItemAngle } from '../../_pure/ringLayout.js';
import { resolveSpokeItemOffsets } from './spokeLayout.js';
export { resolveSpokeItemOffsets } from './spokeLayout.js';
const DEFAULT_GUIDE_SEGMENTS = 128;
const DEFAULT_GUIDE_OPACITY = 0.35;
const DEFAULT_GUIDE_COLOR = 'white';
const DEFAULT_GUIDE_WIDTH = 1;
const IDENTITY_ROTATION = [0, 0, 0];
const ORIGIN = [0, 0, 0];
function toEuler(rotation) {
    return rotation ?? IDENTITY_ROTATION;
}
function quaternionFromEuler(rotation) {
    return new THREE.Quaternion().setFromEuler(new THREE.Euler(rotation[0], rotation[1], rotation[2]));
}
function readGuideState(args) {
    const { constellationState, field } = args;
    if (!constellationState || !(field in constellationState))
        return null;
    const guide = constellationState[field];
    if (guide === undefined || guide === false || guide === null)
        return null;
    return guide;
}
function guideMaterialSpec(guide) {
    return {
        materialKind: 'line-basic',
        color: guide.color ?? DEFAULT_GUIDE_COLOR,
        opacity: guide.opacity ?? DEFAULT_GUIDE_OPACITY,
        width: guide.width ?? DEFAULT_GUIDE_WIDTH,
    };
}
function buildArcGuide(args) {
    const { constellationKey, guide, radius, y, angleRange } = args;
    const segments = guide.segments ?? DEFAULT_GUIDE_SEGMENTS;
    const points = [];
    if (angleRange) {
        for (let i = 0; i < segments; i += 1) {
            const angle0 = angleRange.start + (angleRange.end - angleRange.start) * (i / segments);
            const angle1 = angleRange.start + (angleRange.end - angleRange.start) * ((i + 1) / segments);
            points.push([Math.cos(angle0) * radius, y, Math.sin(angle0) * radius]);
            points.push([Math.cos(angle1) * radius, y, Math.sin(angle1) * radius]);
        }
    }
    else {
        for (let i = 0; i < segments; i += 1) {
            const angle = (i / segments) * Math.PI * 2;
            points.push([Math.cos(angle) * radius, y, Math.sin(angle) * radius]);
        }
    }
    return {
        cacheKey: [
            'guide',
            constellationKey,
            radius,
            y,
            segments,
            angleRange ? `${angleRange.start}:${angleRange.end}` : 'full',
        ].join(':'),
        points,
        closed: angleRange === null,
        material: guideMaterialSpec(guide),
    };
}
function resolveCrescentItemArcSpan(layout) {
    if (typeof layout.equatorGap === 'number') {
        return Math.max(0, Math.PI - layout.equatorGap * 2);
    }
    return layout.arcSpan ?? Math.PI;
}
function resolveCrescentAnchorTransform(args) {
    const { layout, context } = args;
    const { anchor } = layout;
    if (anchor.anchorKind === 'origin') {
        return { position: ORIGIN, yaw: 0 };
    }
    if (anchor.anchorKind !== 'item')
        return null;
    const anchorItemKey = anchor.anchorItemKey;
    const anchorConstellation = context.entityConstellations.find((candidate) => {
        const candidateItems = context.entityItemsByCollectionKey.get(candidate.itemCollectionKey) ?? [];
        return candidateItems.some((item) => item.itemKey === anchorItemKey);
    });
    if (!anchorConstellation)
        return null;
    const anchorLayout = context.entityConstellationParametricStateByKey?.[anchorConstellation.constellationKey]?.layout;
    if (!anchorLayout)
        return null;
    if (anchorLayout.anchor.anchorKind !== 'origin')
        return null;
    const anchorItems = context.entityItemsByCollectionKey.get(anchorConstellation.itemCollectionKey) ?? [];
    if (anchorItems.length === 0)
        return null;
    const anchorIndex = anchorItems.findIndex((item) => item.itemKey === anchorItemKey);
    if (anchorIndex < 0)
        return null;
    const anchorEntityItemKey = anchorItemKey;
    const theta = anchorLayout.layoutKind === 'ring'
        ? resolveRingItemAngle({
            layout: anchorLayout,
            itemKey: anchorEntityItemKey,
            itemIndex: anchorIndex,
            itemCount: anchorItems.length,
        })
        : (anchorIndex / anchorItems.length) * Math.PI * 2;
    const yaw = Math.atan2(-Math.sin(theta), Math.cos(theta));
    if (anchorLayout.layoutKind === 'ring') {
        return {
            position: [Math.cos(theta) * anchorLayout.radius, 0, Math.sin(theta) * anchorLayout.radius],
            yaw,
        };
    }
    if (anchorLayout.layoutKind === 'sector-ring') {
        return {
            position: [Math.cos(theta) * anchorLayout.outerRadius, 0, Math.sin(theta) * anchorLayout.outerRadius],
            yaw,
        };
    }
    return null;
}
function resolveSpokeOrigin(args) {
    const { layout, context } = args;
    if (layout.anchor.anchorKind !== 'parametric') {
        return layout.originNearestPosition ?? ORIGIN;
    }
    const resolved = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
    return resolved ?? layout.originNearestPosition ?? ORIGIN;
}
function normalizeDirection(direction) {
    const vector = new THREE.Vector3(direction[0], direction[1], direction[2]);
    if (vector.lengthSq() < 1e-12)
        return vector.set(1, 0, 0);
    return vector.normalize();
}
function resolvePanelYaw(direction) {
    const planarLength = Math.hypot(direction.x, direction.z);
    if (!Number.isFinite(planarLength) || planarLength <= 1e-8)
        return 0;
    // PlaneGeometry normals start on +Z; yaw turns the panel normal onto the radial direction.
    return Math.atan2(direction.x / planarLength, direction.z / planarLength);
}
/**
 * Resolves the complete render placement of one entity constellation.
 * All layout-specific geometry of the scene graph lives here; renderers only consume the result.
 */
export function resolveConstellationPlacement(args) {
    const { constellation, constellationState, layout, items, shapeByItemKey, spokeSpacingFallback, context } = args;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    if (layout.layoutKind === 'ring') {
        const framePosition = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
        if (framePosition === null)
            return null;
        const guideState = readGuideState({ constellationState, field: 'ringGuide' });
        const ringRotation = toEuler(layout.rotation);
        const ringQuaternion = quaternionFromEuler(ringRotation);
        return {
            framePosition,
            frameRotation: IDENTITY_ROTATION,
            itemFrameRotation: ringRotation,
            itemPlacements: items.map((item, index) => {
                const angle = resolveRingItemAngle({
                    layout,
                    itemKey: item.itemKey,
                    itemIndex: index,
                    itemCount,
                });
                const position = [
                    Math.cos(angle) * layout.radius,
                    0,
                    Math.sin(angle) * layout.radius,
                ];
                const facing = layout.panelFacing && shapeByItemKey.get(item.itemKey)?.shapeKind === 'panel'
                    ? (layout.panelFacing === 'inward' ? -1 : 1) : 0;
                const worldOffset = new THREE.Vector3(...position).applyQuaternion(ringQuaternion);
                return {
                    itemKey: item.itemKey,
                    index,
                    position,
                    rotation: facing
                        ? [0, resolvePanelYaw(new THREE.Vector3(facing * Math.cos(angle), 0, facing * Math.sin(angle))), 0]
                        : IDENTITY_ROTATION,
                    labelFrameRotation: null,
                    labelAnchorWorldPosition: [
                        framePosition[0] + worldOffset.x,
                        framePosition[1] + worldOffset.y,
                        framePosition[2] + worldOffset.z,
                    ],
                };
            }),
            guide: guideState
                ? buildArcGuide({
                    constellationKey: constellation.constellationKey,
                    guide: guideState,
                    radius: layout.radius,
                    y: guideState.y ?? 0,
                    angleRange: null,
                })
                : null,
        };
    }
    if (layout.layoutKind === 'crescent') {
        const anchorTransform = resolveCrescentAnchorTransform({ layout, context });
        if (!anchorTransform)
            return null;
        const crescentRotation = toEuler(layout.rotation);
        const crescentRotationQuaternion = quaternionFromEuler(crescentRotation);
        const anchorYawQuaternion = quaternionFromEuler([0, anchorTransform.yaw, 0]);
        const labelFrameRotation = anchorYawQuaternion.clone().multiply(crescentRotationQuaternion);
        const itemArcSpan = resolveCrescentItemArcSpan(layout);
        const guideArcSpan = layout.arcSpan ?? Math.PI;
        const startAngle = layout.arcCenterAngle - itemArcSpan / 2;
        const endAngle = layout.arcCenterAngle + itemArcSpan / 2;
        const guideState = readGuideState({ constellationState, field: 'crescentGuide' });
        return {
            framePosition: anchorTransform.position,
            frameRotation: [0, anchorTransform.yaw, 0],
            itemFrameRotation: crescentRotation,
            itemPlacements: items.map((item, index) => {
                const angle = itemCount <= 1
                    ? (startAngle + endAngle) / 2
                    : startAngle + (index / (itemCount - 1)) * (endAngle - startAngle);
                const position = [
                    Math.cos(angle) * layout.radius,
                    0,
                    Math.sin(angle) * layout.radius,
                ];
                const worldPosition = new THREE.Vector3(position[0], position[1], position[2])
                    .applyQuaternion(crescentRotationQuaternion)
                    .applyQuaternion(anchorYawQuaternion)
                    .add(new THREE.Vector3(...anchorTransform.position));
                return {
                    itemKey: item.itemKey,
                    index,
                    position,
                    rotation: IDENTITY_ROTATION,
                    labelFrameRotation,
                    labelAnchorWorldPosition: [worldPosition.x, worldPosition.y, worldPosition.z],
                };
            }),
            guide: guideState
                ? buildArcGuide({
                    constellationKey: constellation.constellationKey,
                    guide: guideState,
                    radius: layout.radius,
                    y: 0,
                    angleRange: {
                        start: layout.arcCenterAngle - guideArcSpan / 2,
                        end: layout.arcCenterAngle + guideArcSpan / 2,
                    },
                })
                : null,
        };
    }
    if (layout.layoutKind === 'spoke') {
        const direction = normalizeDirection(layout.direction);
        const panelYaw = resolvePanelYaw(direction);
        const spacing = layout.spacing > 0 ? layout.spacing : spokeSpacingFallback;
        const offsets = resolveSpokeItemOffsets({ itemCount, spacing, itemOffsets: layout.itemOffsets });
        return {
            framePosition: resolveSpokeOrigin({ layout, context }),
            frameRotation: toEuler(layout.rotation),
            itemFrameRotation: IDENTITY_ROTATION,
            itemPlacements: items.map((item, index) => {
                const offset = offsets[index] ?? spacing * index;
                const isPanel = shapeByItemKey.get(item.itemKey)?.shapeKind === 'panel';
                return {
                    itemKey: item.itemKey,
                    index,
                    position: [direction.x * offset, direction.y * offset, direction.z * offset],
                    rotation: isPanel ? [0, panelYaw, 0] : IDENTITY_ROTATION,
                    labelFrameRotation: null,
                    labelAnchorWorldPosition: null,
                };
            }),
            guide: null,
        };
    }
    if (layout.layoutKind === 'sector-ring') {
        const framePosition = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
        if (framePosition === null)
            return null;
        const ringRotation = toEuler(layout.rotation);
        const ringRotationQuaternion = quaternionFromEuler(ringRotation);
        return {
            framePosition,
            frameRotation: IDENTITY_ROTATION,
            itemFrameRotation: ringRotation,
            itemPlacements: items.map((item, index) => {
                const angle = (index / itemCount) * Math.PI * 2;
                // Local Y is the ring normal, so the offset is unaffected by the item's angular spin.
                const normalOffset = args.layoutNormalOffsetByItemKey?.get(item.itemKey) ?? 0;
                return {
                    itemKey: item.itemKey,
                    index,
                    position: normalOffset === 0 ? ORIGIN : [0, normalOffset, 0],
                    rotation: [0, angle, 0],
                    labelFrameRotation: ringRotationQuaternion.clone().multiply(quaternionFromEuler([0, angle, 0])),
                    labelAnchorWorldPosition: ORIGIN,
                };
            }),
            guide: null,
        };
    }
    if (layout.anchor.anchorKind !== 'origin')
        return null;
    const ringRotation = toEuler(layout.rotation);
    const ringRotationQuaternion = quaternionFromEuler(ringRotation);
    return {
        framePosition: ORIGIN,
        frameRotation: IDENTITY_ROTATION,
        itemFrameRotation: ringRotation,
        itemPlacements: items.map((item, index) => ({
            itemKey: item.itemKey,
            index,
            position: ORIGIN,
            rotation: IDENTITY_ROTATION,
            labelFrameRotation: ringRotationQuaternion,
            labelAnchorWorldPosition: ORIGIN,
        })),
        guide: null,
    };
}
